import { useState, useCallback, useMemo } from 'react';
import { distance, projectPointOnLine, midpoint } from '../utils/geometry';

// Snap-Prioritäten (niedriger = höher)
const SNAP_PRIORITY = {
  origin: 1,
  end: 2,
  mid: 3,
  int: 4,
  perp: 5,
  near: 6,
  grid: 7,
};

// Snap-Farben für visuelles Feedback
export const SNAP_COLORS = {
  origin: '#ef4444', // Rot
  end: '#eab308',    // Gelb
  mid: '#06b6d4',    // Cyan
  int: '#f97316',    // Orange
  perp: '#22c55e',   // Grün
  near: '#8b5cf6',   // Violett
  grid: '#6b7280',   // Grau
};

/**
 * Berechnet den Schnittpunkt zweier Linien.
 * @returns {Object|null} { x, y, t1, t2 } oder null wenn parallel
 * t1, t2 sind die Parameter (0-1 = auf Linie)
 */
function lineIntersection(p1, p2, p3, p4) {
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y;
  const x4 = p4.x, y4 = p4.y;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (Math.abs(denom) < 0.0001) {
    return null; // Linien sind parallel
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  const x = x1 + t * (x2 - x1);
  const y = y1 + t * (y2 - y1);

  return { x, y, t1: t, t2: u };
}

/**
 * Findet den senkrechten Punkt auf einer Linie von einem gegebenen Punkt.
 * @returns {Object|null} { x, y, t } oder null wenn außerhalb
 */
function perpendicularPoint(point, lineStart, lineEnd) {
  const proj = projectPointOnLine(point, lineStart, lineEnd);

  // Nur gültig wenn auf der Linie (t zwischen 0 und 1)
  if (proj.t >= 0 && proj.t <= 1) {
    return proj;
  }

  return null;
}

/**
 * Unified Snap Hook - Extrahiert aus useCADSettings mit erweiterten OSnaps.
 */
export function useSnap(cadSettings, snapRadius = 300) {
  // Aktiver Snap für visuelles Feedback
  const [activeSnap, setActiveSnap] = useState(null);

  const { orthoMode, gridSnap, gridSize, osnap, lastPoint } = cadSettings;

  /**
   * Berechnet alle Snap-Kandidaten und gibt den besten zurück.
   * @param {number} x
   * @param {number} y
   * @param {Array} points - Building points
   * @param {Array} walls - Building walls
   * @param {Object} geometryData - Optional: { points, curves } from geometry state
   */
  const getSnappedPosition = useCallback((x, y, points = [], walls = [], geometryData = null) => {
    const candidates = [];

    // 0. Origin-Snap (höchste Priorität)
    const distToOrigin = Math.sqrt(x ** 2 + y ** 2);
    if (distToOrigin < snapRadius) {
      candidates.push({
        x: 0,
        y: 0,
        type: 'origin',
        priority: SNAP_PRIORITY.origin,
        distance: distToOrigin,
      });
    }

    // Object Snaps nur wenn aktiviert
    if (osnap.enabled) {
      // 1. End-Snap (Endpunkte) - building points + geometry points
      if (osnap.end) {
        for (const point of points) {
          const dist = distance({ x, y }, point);
          if (dist < snapRadius) {
            candidates.push({
              x: point.x,
              y: point.y,
              z: point.z,
              type: 'end',
              priority: SNAP_PRIORITY.end,
              distance: dist,
              ref: point,
            });
          }
        }

        // Geometry points (from useGeometryState)
        if (geometryData?.points) {
          for (const point of geometryData.points) {
            const dist = distance({ x, y }, point);
            if (dist < snapRadius) {
              candidates.push({
                x: point.x,
                y: point.y,
                z: point.z,
                type: 'end',
                priority: SNAP_PRIORITY.end,
                distance: dist,
                ref: point,
              });
            }
          }
        }
      }

      // 2. Mid-Snap (Wandmitten + Geometry curve midpoints)
      if (osnap.mid) {
        for (const wall of walls) {
          const start = points.find(p => p.id === wall.startPointId);
          const end = points.find(p => p.id === wall.endPointId);
          if (start && end) {
            const mid = midpoint(start, end);
            const dist = distance({ x, y }, mid);
            if (dist < snapRadius) {
              const midZ = ((start.z ?? 0) + (end.z ?? 0)) / 2;
              candidates.push({
                x: mid.x,
                y: mid.y,
                z: midZ || undefined,
                type: 'mid',
                priority: SNAP_PRIORITY.mid,
                distance: dist,
                ref: wall,
              });
            }
          }
        }

        // Geometry curve midpoints (Lines, Polyline segments)
        if (geometryData?.curves) {
          for (const curve of geometryData.curves) {
            if (curve.type === 'Line') {
              const s = curve._start || curve.start;
              const e = curve._end || curve.end;
              if (s && e) {
                const mid = midpoint(s, e);
                const dist = distance({ x, y }, mid);
                if (dist < snapRadius) {
                  const midZ = ((s.z ?? 0) + (e.z ?? 0)) / 2;
                  candidates.push({
                    x: mid.x,
                    y: mid.y,
                    z: midZ || undefined,
                    type: 'mid',
                    priority: SNAP_PRIORITY.mid,
                    distance: dist,
                    ref: curve,
                  });
                }
              }
            } else if (curve.type === 'Polyline' && curve._points) {
              // Midpoint of each segment
              for (let i = 0; i < curve._points.length - 1; i++) {
                const s = curve._points[i];
                const e = curve._points[i + 1];
                const mid = midpoint(s, e);
                const dist = distance({ x, y }, mid);
                if (dist < snapRadius) {
                  const midZ = ((s.z ?? 0) + (e.z ?? 0)) / 2;
                  candidates.push({
                    x: mid.x,
                    y: mid.y,
                    z: midZ || undefined,
                    type: 'mid',
                    priority: SNAP_PRIORITY.mid,
                    distance: dist,
                    ref: curve,
                  });
                }
              }
              // Closing segment if closed
              if (curve._closed && curve._points.length > 2) {
                const s = curve._points[curve._points.length - 1];
                const e = curve._points[0];
                const mid = midpoint(s, e);
                const dist = distance({ x, y }, mid);
                if (dist < snapRadius) {
                  candidates.push({
                    x: mid.x,
                    y: mid.y,
                    type: 'mid',
                    priority: SNAP_PRIORITY.mid,
                    distance: dist,
                    ref: curve,
                  });
                }
              }
            }
          }
        }
      }

      // 3. Int-Snap (Schnittpunkte)
      if (osnap.int) {
        // Wand-Wand Schnittpunkte
        for (let i = 0; i < walls.length; i++) {
          for (let j = i + 1; j < walls.length; j++) {
            const wall1 = walls[i];
            const wall2 = walls[j];

            const s1 = points.find(p => p.id === wall1.startPointId);
            const e1 = points.find(p => p.id === wall1.endPointId);
            const s2 = points.find(p => p.id === wall2.startPointId);
            const e2 = points.find(p => p.id === wall2.endPointId);

            if (s1 && e1 && s2 && e2) {
              const intersection = lineIntersection(s1, e1, s2, e2);

              if (intersection) {
                // Nur wenn Schnittpunkt auf beiden Linien liegt (oder nahe dran)
                const onLine1 = intersection.t1 >= -0.1 && intersection.t1 <= 1.1;
                const onLine2 = intersection.t2 >= -0.1 && intersection.t2 <= 1.1;

                if (onLine1 && onLine2) {
                  const dist = distance({ x, y }, intersection);
                  if (dist < snapRadius) {
                    // Z-Höhe interpolieren
                    const z1 = (s1.z ?? 0) + intersection.t1 * ((e1.z ?? 0) - (s1.z ?? 0));
                    const z2 = (s2.z ?? 0) + intersection.t2 * ((e2.z ?? 0) - (s2.z ?? 0));
                    const avgZ = (z1 + z2) / 2;

                    candidates.push({
                      x: intersection.x,
                      y: intersection.y,
                      z: avgZ || undefined,
                      type: 'int',
                      priority: SNAP_PRIORITY.int,
                      distance: dist,
                      ref: { wall1, wall2 },
                    });
                  }
                }
              }
            }
          }
        }
      }

      // 4. Perp-Snap (Senkrecht auf Linie)
      if (osnap.perp && lastPoint) {
        for (const wall of walls) {
          const start = points.find(p => p.id === wall.startPointId);
          const end = points.find(p => p.id === wall.endPointId);

          if (start && end) {
            const perpPoint = perpendicularPoint(lastPoint, start, end);

            if (perpPoint) {
              const dist = distance({ x, y }, perpPoint);
              if (dist < snapRadius) {
                const perpZ = (start.z ?? 0) + perpPoint.t * ((end.z ?? 0) - (start.z ?? 0));
                candidates.push({
                  x: perpPoint.x,
                  y: perpPoint.y,
                  z: perpZ || undefined,
                  type: 'perp',
                  priority: SNAP_PRIORITY.perp,
                  distance: dist,
                  ref: wall,
                });
              }
            }
          }
        }
      }

      // 5. Near-Snap (Nächster Punkt auf Linie)
      if (osnap.near) {
        for (const wall of walls) {
          const start = points.find(p => p.id === wall.startPointId);
          const end = points.find(p => p.id === wall.endPointId);

          if (start && end) {
            const nearPoint = projectPointOnLine({ x, y }, start, end);

            // Nur wenn auf der Linie
            if (nearPoint.t >= 0 && nearPoint.t <= 1) {
              const dist = distance({ x, y }, nearPoint);
              if (dist < snapRadius) {
                const nearZ = (start.z ?? 0) + nearPoint.t * ((end.z ?? 0) - (start.z ?? 0));
                candidates.push({
                  x: nearPoint.x,
                  y: nearPoint.y,
                  z: nearZ || undefined,
                  type: 'near',
                  priority: SNAP_PRIORITY.near,
                  distance: dist,
                  ref: wall,
                });
              }
            }
          }
        }
      }
    }

    // Besten Kandidaten auswählen (nach Priorität, dann Distanz)
    candidates.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return a.distance - b.distance;
    });

    // Wenn OSnap-Kandidat gefunden, verwenden
    if (candidates.length > 0 && candidates[0].priority < SNAP_PRIORITY.grid) {
      const best = candidates[0];
      setActiveSnap({
        type: best.type,
        x: best.x,
        y: best.y,
        color: SNAP_COLORS[best.type],
      });
      return {
        x: best.x,
        y: best.y,
        z: best.z,
        snap: { type: best.type, ref: best.ref },
      };
    }

    // Fallback: Grid-Snap
    let snappedX = x;
    let snappedY = y;
    let snapInfo = null;

    if (gridSnap) {
      snappedX = Math.round(x / gridSize) * gridSize;
      snappedY = Math.round(y / gridSize) * gridSize;
      snapInfo = { type: 'grid' };
    }

    // Ortho-Modus
    if (orthoMode && lastPoint) {
      const dx = Math.abs(snappedX - lastPoint.x);
      const dy = Math.abs(snappedY - lastPoint.y);

      if (dx > dy) {
        snappedY = lastPoint.y;
      } else {
        snappedX = lastPoint.x;
      }
      snapInfo = { ...snapInfo, ortho: true };
    }

    // Kein OSnap aktiv
    if (gridSnap || (orthoMode && lastPoint)) {
      setActiveSnap({
        type: snapInfo?.type || 'grid',
        x: snappedX,
        y: snappedY,
        color: SNAP_COLORS.grid,
      });
    } else {
      setActiveSnap(null);
    }

    return { x: snappedX, y: snappedY, snap: snapInfo };
  }, [orthoMode, gridSnap, gridSize, osnap, lastPoint, snapRadius]);

  // Snap-Typ-Labels für UI
  const activeSnapTypes = useMemo(() => {
    const types = [];
    if (osnap.enabled) {
      if (osnap.end) types.push('END');
      if (osnap.mid) types.push('MID');
      if (osnap.int) types.push('INT');
      if (osnap.perp) types.push('PERP');
      if (osnap.near) types.push('NEAR');
    }
    return types;
  }, [osnap]);

  return {
    getSnappedPosition,
    activeSnap,
    activeSnapTypes,
    SNAP_COLORS,
  };
}

export default useSnap;
